<script setup lang="ts">
import {computed, ref} from "vue";
import MedicationCheckupCopiloteLine from "@/medication-checkup/components/MedicationCheckupCopiloteLine.vue";
import MedicationCheckupUserEcho from "@/medication-checkup/components/MedicationCheckupUserEcho.vue";
import MedicationCheckupCopiloteBubble from "@/medication-checkup/components/MedicationCheckupCopiloteBubble.vue";
import MedicationCheckupSceneReplies from "@/medication-checkup/components/MedicationCheckupSceneReplies.vue";
import MedicationCheckupCounsellingPlanCard from "@/medication-checkup/components/MedicationCheckupCounsellingPlanCard.vue";
import MedicationCheckupCounsellingPlanFullScreen from "@/medication-checkup/components/MedicationCheckupCounsellingPlanFullScreen.vue";
import MedicationCheckupDosageDialog from "@/medication-checkup/components/MedicationCheckupDosageDialog.vue";
import {MedicationCheckupAction} from "@/medication-checkup/interfaces";
import {CounsellingPlanDraft, CounsellingPlanLine, CounsellingPlanRow, CounsellingPlanView} from "@/medication-checkup/interfaces/counselling-plan";
import {DosageDialogLine, TreatmentLineDosageDraft} from "@/medication-checkup/interfaces/treatment-selections";
import {SceneProps, useScene} from "@/medication-checkup/composables/useScene";
import {useMedicationCheckupStore} from "@/medication-checkup/stores/medication-checkup-store";
import CounsellingPlanService from "@/medication-checkup/services/CounsellingPlanService";
import TreatmentLineDosageService from "@/medication-checkup/services/TreatmentLineDosageService";
import useCustomer from "@/customer/composables/useCustomer";

/**
 * The scene of the posology plan of the advice interview: the treatments as
 * the analysis left them, the posologies to enter for what changes, the
 * advice for the patient, and the plan shown to them full screen.
 *
 * Which line needs or may take a posology, and in what order the lines come,
 * is served by the HDS. Entering is the scene's own state — the dosage
 * dialog of the collection, the advice typed in the card — until the one
 * action writes the whole plan; the full screen shows the plan as it stands,
 * drafts included.
 */

const props = defineProps<SceneProps<CounsellingPlanView>>();

const {bubbleShown, cardShown, clicked, view, text, actions, acted, act: submitBlock} = useScene(
    props,
    {card: true}
);

const store = useMedicationCheckupStore();

const {data: customer} = useCustomer(store.customerId ?? undefined);

const submitting = ref(false);

const warning = ref<string | null>(null);

const drafts = ref<Record<string, CounsellingPlanDraft>>({});

const fullscreen = ref(false);

/**
 * The line whose posology is being entered.
 */
const editing = ref<CounsellingPlanLine | null>(null);

/**
 * The same line as the dosage dialog takes one, set once as the edition
 * opens: the product the doses are offered for, the name of what is taken
 * from now on, and the posology the form opens on — the collected one on a
 * modified posology.
 */
const dialogLine = ref<DosageDialogLine | null>(null);

const lines = computed(() => view.value?.lines ?? []);

const draftOf = (line: CounsellingPlanLine): CounsellingPlanDraft => {
    return drafts.value[CounsellingPlanService.key(line)] ?? {dosage: null, advice: line.advice ?? ''};
};

const update = (line: CounsellingPlanLine, patch: Partial<CounsellingPlanDraft>) => {
    drafts.value = {...drafts.value, [CounsellingPlanService.key(line)]: {...draftOf(line), ...patch}};
    warning.value = null;
};

const edit = (line: CounsellingPlanLine) => {
    if (line.product_id === null) {
        return;
    }

    editing.value = line;
    dialogLine.value = {
        id: lines.value.indexOf(line),
        product_id: line.product_id,
        label: line.replacement ?? line.label,
        dosage: line.seed,
        dosage_proposal: null,
    };
};

const closeDosage = () => {
    editing.value = null;
    dialogLine.value = null;
};

const saveDosage = (draft: TreatmentLineDosageDraft) => {
    if (editing.value) {
        update(editing.value, {dosage: draft});
    }

    closeDosage();
};

/**
 * The lines the patient sees full screen, posologies and advice resolved.
 */
const shown = computed(() => lines.value.map(line => ({
    line,
    posology: props.frozen ? line.posology : CounsellingPlanService.posology(line, drafts.value[CounsellingPlanService.key(line)]),
    advice: props.frozen ? line.advice : CounsellingPlanService.advice(line, drafts.value[CounsellingPlanService.key(line)]),
})));

const shownDate = computed(() => {
    const date = view.value?.validated_at ? new Date(view.value.validated_at) : new Date();

    return new Intl.DateTimeFormat('fr-FR', {day: 'numeric', month: 'long', year: 'numeric'}).format(date);
});

/**
 * The treatments whose posology is still to enter, by name.
 */
const missing = computed(() => lines.value
    .filter(line => line.dosage_required && !drafts.value[CounsellingPlanService.key(line)]?.dosage)
    .map(line => line.replacement ?? line.label)
);

const rows = (): CounsellingPlanRow[] => lines.value.map(line => {
    const draft = draftOf(line);

    return {
        intervention_id: line.intervention_id,
        doctor_decision_id: line.doctor_decision_id,
        dosage: draft.dosage && line.product_id !== null
            ? TreatmentLineDosageService.toDosage(line.product_id, draft.dosage)
            : null,
        advice: draft.advice.trim() || null,
    };
});

/**
 * Validate the plan, refused here while a changed treatment has no
 * posology — naming them, as the server would.
 */
const act = async (action: MedicationCheckupAction) => {
    if (missing.value.length > 0) {
        warning.value = `Posologie à saisir : ${missing.value.join(', ')}.`;

        return;
    }

    submitting.value = true;

    try {
        await submitBlock(action, {lines: rows()});
    } finally {
        submitting.value = false;
    }
};
</script>

<template>
  <div class="flex flex-col gap-3.5">
    <template v-if="frozen">
      <MedicationCheckupCopiloteLine v-if="text" :text="text" />
      <MedicationCheckupCounsellingPlanCard
        v-if="view"
        :view="view"
        frozen
        @fullscreen="fullscreen = true"
      />
      <MedicationCheckupUserEcho v-if="acted" :text="acted" />
    </template>

    <template v-else>
      <MedicationCheckupCopiloteBubble
        :shown="bubbleShown"
        :text="text"
      />

      <MedicationCheckupCounsellingPlanCard
        v-if="cardShown && view"
        :view="view"
        :drafts="drafts"
        :busy="submitting"
        class="animate-message-in"
        @edit="edit"
        @advice="(line, advice) => update(line, {advice})"
        @fullscreen="fullscreen = true"
      />

      <span
        v-if="warning"
        class="ml-10 text-xs font-semibold text-red-light animate-message-in"
      >
        {{ warning }}
      </span>

      <MedicationCheckupSceneReplies
        :clicked="clicked"
        :actions="actions"
        :shown="cardShown"
        :disabled="submitting"
        @act="act"
      />
    </template>

    <MedicationCheckupDosageDialog
      :line="dialogLine"
      :draft="editing ? draftOf(editing).dosage : null"
      :instruction="editing?.after ?? null"
      @close="closeDosage"
      @save="saveDosage"
    />

    <MedicationCheckupCounsellingPlanFullScreen
      v-if="fullscreen && view"
      :lines="shown"
      :patient="customer?.information?.name ?? ''"
      :date="shownDate"
      @close="fullscreen = false"
    />
  </div>
</template>
